import React, { useState, useEffect, useLayoutEffect } from 'react';
import { gql, useMutation, useQuery, useSuspenseQuery } from '@apollo/client';
import { Modal, ModalAction, useModalContext } from '../common/Modal';
import { VariationsMatchingModal_GetVariationTypesQuery, VariationsMatchingModal_GetVariationTypesQueryVariables } from '../../../codegen/graphql';
import { toast } from 'react-toastify';
import { getTranslatedValue } from '../common/TranslatedValue';

const LINK_AD_VARIATION_TO_SUPPLIER_PRODUCT_VARIATION = gql`
  mutation LinkAdVariationToSupplierProductVariation($adVariationId: ID!, $supplierProductVariationKeys: [SupplierProductVariationCompositeKey!]!) {
    link_AdVariation_SupplierProductVariation(adVariationId: $adVariationId, supplierProductVariationKeys: $supplierProductVariationKeys) {
      id
    }
  }
`;

const GET_VARIATION_TYPES = gql`
  query VariationsMatchingModal_GetVariationTypes($adId: ID!, $supplierProductId: String!) {

    ad(id: $adId) {
      id

      variationTypes {
        id
        type
        typeValues {
          id
          value
          medias {
            id
            url
          }
        }
      }

      variations {
        id
        variationTypeValues {
          id
          type {
            id
          }
          medias {
            id
            url
          }
        }
      }
    }

    supplierProduct(id: $supplierProductId) {
      id
      variationTypes {
        id
        type
        typeValues {
          id
          value
          extraData
        }
      }

      variations {
        sku
        supplierProductId
        typeValues {
          id
          type {
            id
          }
        }
      }
    }
  }
`;

interface TypeMapping {
  mappedAdTypeId: string | null;
  useSingleValue: boolean;
  singleValueId: string | null | undefined;
}

interface TypeValueMapping {
  mappedAdTypeValueId: string | null;
}

interface VariationMatchingModalProps {
  onClose: () => void;
  adId: string;
  supplierProductId: string;
}

export function VariationMatchingModal({ onClose, adId, supplierProductId }: VariationMatchingModalProps) {
  return (
    <Modal
        title="Match Variations"
        onClose={onClose}
        width="large"
      >
        <VariationMatchingModalContent
          onClose={onClose}
          adId={adId}
          supplierProductId={supplierProductId}
        />
      </Modal>
  );
}

export function VariationMatchingModalContent({ onClose, adId, supplierProductId }: VariationMatchingModalProps) {
  const [linkAdVariationToSupplierProductVariation] = useMutation(LINK_AD_VARIATION_TO_SUPPLIER_PRODUCT_VARIATION);
  const [selectWithImageModalOpen, setSelectWithImageModalOpen] = useState(false);
  const [selectWithImageModalState, setSelectWithImageModalState] = useState<{
    supplierTypeId: string;
    supplierTypeValueId: string;
    supplierTypeValueImage: string | null;
  } | null>(null);
  
  const { data: queryData } = useSuspenseQuery<
    VariationsMatchingModal_GetVariationTypesQuery,
    VariationsMatchingModal_GetVariationTypesQueryVariables
  >(GET_VARIATION_TYPES, {
    variables: { adId, supplierProductId },
  });


  const [typesMappings, setTypesMappings] = useState<{
    [supplierTypeId: string]: TypeMapping;
  }>(() => {
    return queryData.supplierProduct!.variationTypes.reduce<{
      [supplierTypeId: string]: TypeMapping;
    }>((acc, type) => {
      acc[type.id] = {
        mappedAdTypeId: null,
        useSingleValue: false,
        singleValueId: null,
      };
      return acc;
    }, {});
  });

  console.log(typesMappings);

  const [typesValuesMappings, setTypesValuesMappings] = useState<{
    [supplierTypeValueId: string]: TypeValueMapping;
  }>(() => {
    return queryData.supplierProduct!.variationTypes.reduce<{
      [supplierTypeValueId: string]: TypeValueMapping;
    }>((acc, type) => {
      type.typeValues.forEach(value => {
        acc[value.id] = {
          mappedAdTypeValueId: null,
        };
      });
      return acc;
    }, {});
  });


  const handleMappedAdTypeIdChange = (supplierTypeId: string, mappedAdTypeId: string) => {
    setTypesMappings(prev => {
      return {
        ...prev,
        [supplierTypeId]: {
          ...prev[supplierTypeId],
          mappedAdTypeId: mappedAdTypeId
        }
      }
    });
  };

  const handleMappedAdTypeValueIdChange = (supplierTypeValueId: string, mappedAdTypeValueId: string) => {
    setTypesValuesMappings(prev => {
      return {
        ...prev,
        [supplierTypeValueId]: {
          ...prev[supplierTypeValueId],
          mappedAdTypeValueId: mappedAdTypeValueId
        }
      }
    });
  };

  const handleUseSingleValueToggle = (
    supplierTypeId: string,
  ) => {
    setTypesMappings(prev => {
      return {
        ...prev,
        [supplierTypeId]: {
          ...prev[supplierTypeId],
          useSingleValue: !prev[supplierTypeId].useSingleValue,
          singleValueId: null,
        }
      }
    });
  };

  const handleSingleValueChange = (supplierTypeId: string, singleValueId: string) => {
    setTypesMappings(prev => {
      return {
        ...prev,
        [supplierTypeId]: {
          ...prev[supplierTypeId],
          singleValueId: singleValueId,
        }
      }
    });
  };

  const handleMatch = async () => {
    try {
      // const reversedTypesMappings: { [adTypeId: string]: string } = {}
      // const reversedTypesValuesMappings: { [adTypeValueId: string]: string } = {}
      // const reversedSingleTypeValueMappings: { [adTypeValueId: string]: boolean } = {}
      
      // for (const [supplierTypeId, typeMapping] of Object.entries(typesMappings)) {
      //   if (typeMapping.mappedAdTypeId) {
      //     reversedTypesMappings[typeMapping.mappedAdTypeId] = supplierTypeId;
      //   }

      //   if (typeMapping.singleValueId) {
      //     reversedSingleTypeValueMappings[typeMapping.singleValueId] = true;
      //   }
      // }

      // for (const [supplierTypeValueId, typeValueMapping] of Object.entries(typesValuesMappings)) {
      //   if (typeValueMapping.mappedAdTypeValueId) {
      //     reversedTypesValuesMappings[typeValueMapping.mappedAdTypeValueId] = supplierTypeValueId;
      //   }
      // }

      // We'll use this to track successful matches
      let matchedCount = 0;
      let skippedCount = 0;
      
      // Match each supplier variation to the corresponding ad variation
      const matchPromises = queryData.supplierProduct!.variations.map(async (supplierVar) => {
        if (!supplierVar.typeValues) {
          skippedCount++;
          return null;
        }
        
        // Find all ad variation type values that match this supplier variation
        const matchingAdTypeValueIds: string[] = [];
        let allPropertiesMatched = true;
        
        for (const typeValue of supplierVar.typeValues) {
          // Check if this property uses a single value
          if (typesMappings[typeValue.type.id]?.singleValueId) {
            // Skip this variation if it doesn't match the single value
            if (typeValue.id !== typesMappings[typeValue.type.id]?.singleValueId) {
              allPropertiesMatched = false;
              break;
            }
            continue;
          }

          
          
          // Skip if we don't have a mapping for this property
          if (!typesMappings[typeValue.type.id]?.mappedAdTypeId) {
            allPropertiesMatched = false;
            break;
          }
          
          // Skip if we don't have a mapping for this property value
          if (!typesValuesMappings[typeValue.id]?.mappedAdTypeValueId) {
            allPropertiesMatched = false;
            break;
          }
          
          matchingAdTypeValueIds.push(typesValuesMappings[typeValue.id].mappedAdTypeValueId!);
        }
        
        if (!allPropertiesMatched || matchingAdTypeValueIds.length === 0) {
          skippedCount++;
          return null;
        }
        
        // Find the ad variation that has exactly these type values
        const matchingAdVariation = queryData.ad!.variations.find((adVar: any) => {
          if (adVar.variationTypeValues.length !== matchingAdTypeValueIds.length) {
            return false;
          }
          
          const adVarTypeValueIds = adVar.variationTypeValues.map((tv: any) => tv.id);
          return (
            matchingAdTypeValueIds.every((id: string) => (
              adVarTypeValueIds.includes(id)
            )) &&
            adVarTypeValueIds.every((id: string) => (
              matchingAdTypeValueIds.includes(id)
            ))
          );
        });
        
        if (!matchingAdVariation) {
          skippedCount++;
          return null;
        }
        
        // Link the supplier variation to the ad variation
        matchedCount++;
        return linkAdVariationToSupplierProductVariation({
          variables: {
            adVariationId: matchingAdVariation.id,
            supplierProductVariationKeys: [{
              sku: supplierVar.sku,
              supplierProductId: supplierVar.supplierProductId,
            }]
          }
        });
      });
      
      await Promise.all(matchPromises.filter(Boolean));
      
      toast.success(`Variations matched: ${matchedCount} matched, ${skippedCount} skipped`);
      onClose();
    } catch (error) {
      console.error("Error matching variations:", error);
      toast.error('Failed to match variations');
    }
  };

  const modalContext = useModalContext();

  useLayoutEffect(() => {
    modalContext.setActions([
      {
        label: 'Cancel',
        onClick: onClose,
        preset: 'white'
      },
      {
        label: 'Match',
        onClick: handleMatch,
        preset: 'primary',
      }
    ])
  }, [modalContext, onClose, handleMatch])

  const openSelectWithImageModal = (
    supplierTypeId: string,
    supplierTypeValueId: string,
    supplierTypeValueImage: string | null
  ) => {
    setSelectWithImageModalState({
      supplierTypeId,
      supplierTypeValueId,
      supplierTypeValueImage,
    });
    setSelectWithImageModalOpen(true);
  };

  const closeSelectWithImageModal = () => {
    setSelectWithImageModalOpen(false);
    setSelectWithImageModalState(null);
  };

  const selectOptionFromModal = (adTypeValueId: string) => {
    if (!selectWithImageModalState) return;
    
    handleMappedAdTypeValueIdChange(
      selectWithImageModalState.supplierTypeValueId,
      adTypeValueId
    );
    
    closeSelectWithImageModal();
  };

  return (
    <>
      <div className="space-y-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="text-blue-800 font-medium mb-2">How this works</h3>
          <p className="text-blue-700 text-sm">
            1. Match each supplier property (like "Color" or "Size") to the corresponding ad variation type.
            <br/>
            2. Then match each possible value (like "Red" or "XL") to the corresponding variation type value.
            <br/>
            3. If a property can't be matched, you can select a single value that will always be used.
            <br/>
            4. When you click "Match", we'll automatically link supplier variations to ad variations based on your mappings.
          </p>
        </div>

        {queryData.ad!.variations.length === 0 && (
          <div className="text-center py-8 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-yellow-700">
              You need to create variation types and values for your ad first.
            </p>
            <p className="text-yellow-600 text-sm mt-2">
              Try using the "Import variations" action to automatically create them.
            </p>
          </div>
        )}

        {Object.keys(typesMappings).length === 0 && (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <p className="text-gray-600">
              No variation properties found for this product.
            </p>
          </div>
        )}
        
        <div className="space-y-4">
          {queryData.supplierProduct!.variationTypes.map((supplierType) => {
            const supplierTypeMapping = typesMappings[supplierType.id];
            const mappedAdType = queryData.ad!.variationTypes.find(
              (t: any) => t.id === supplierTypeMapping.mappedAdTypeId
            );

            return (
              <div key={supplierType.id} className="border rounded-lg p-4">
                <div className="flex items-center mb-4">
                  <div className="w-1/3">
                    <span className="font-medium">{supplierType.type}</span>
                    <span className="text-xs text-gray-500 ml-1">
                      ({supplierType.typeValues.length} values)
                    </span>
                  </div>
                  <div className="w-2/3 flex items-center space-x-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`use-single-${supplierType.id}`}
                        checked={supplierTypeMapping.useSingleValue}
                        onChange={() => handleUseSingleValueToggle(supplierType.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`use-single-${supplierType.id}`} className="ml-2 text-sm text-gray-700">
                        Use single value
                      </label>
                    </div>
                    
                    {!supplierTypeMapping.useSingleValue && (
                      <select
                        className="grow p-2 border rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={supplierTypeMapping.mappedAdTypeId || ''}
                        onChange={(e) => handleMappedAdTypeIdChange(supplierType.id, e.target.value)}
                      >
                        <option value="">Select variation type</option>
                        {queryData.ad!.variationTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {getTranslatedValue(type.type)} ({type.typeValues.length} values)
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {supplierTypeMapping.useSingleValue && (
                  <div className="ml-8 border-l-2 border-blue-200 pl-4">
                    <select
                      className="w-full p-2 border rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={supplierTypeMapping.singleValueId || ''}
                      onChange={(e) => handleSingleValueChange(supplierType.id, e.target.value)}
                    >
                      <option value="">Select a value to use</option>
                      {supplierType.typeValues.map(value => (
                        <option key={value.id} value={value.id}>
                          {value.value}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {!supplierTypeMapping.useSingleValue && mappedAdType && (
                  <div className="ml-8 border-l-2 border-blue-200 pl-4 space-y-3">
                    {supplierType.typeValues.map(supplierTypeValue => {
                      const typeValueMapping = typesValuesMappings[supplierTypeValue.id];
                      const mappedAdTypeValue = mappedAdType?.typeValues.find(
                        (t: any) => t.id === typeValueMapping.mappedAdTypeValueId
                      );
                      
                      return (
                        <div key={supplierTypeValue.id} className="flex items-end">
                          <div className="w-30 flex flex-col items-center space-x-2 border-r-2 border-blue-200 pr-2">
                            <span>{supplierTypeValue.value}</span>
                            {supplierTypeValue.extraData?.image && (
                              <img
                                src={supplierTypeValue.extraData.image}
                                alt={supplierTypeValue.value}
                                className="w-25 h-25 object-cover rounded-sm border"
                                referrerPolicy='no-referrer'
                              />
                            )}
                          </div>
                          <div className="flex-1 pl-2">
                            <div className="relative flex items-end space-x-2">
                              {/* Display selected type value image if available */}
                              {typeValueMapping.mappedAdTypeValueId && (
                                <div className="mt-2 flex space-x-2">
                                  {mappedAdType?.typeValues
                                    .find((tv: any) => tv.id === typeValueMapping.mappedAdTypeValueId)
                                    ?.medias?.map((media: any) => (
                                      <img 
                                        key={media.id}
                                        src={media.url} 
                                        alt={`${supplierTypeValue.value} image`}
                                        className="h-25 w-25 object-cover rounded-sm border"
                                      />
                                    ))
                                  }
                                </div>
                              )}
                              <select
                                className="grow p-2 border rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={typeValueMapping.mappedAdTypeValueId || ''}
                                onChange={(e) => handleMappedAdTypeValueIdChange(
                                  supplierTypeValue.id,
                                  e.target.value
                                )}
                              >
                                <option value="">Select type value</option>
                                {mappedAdType.typeValues.map((typeValue: any) => (
                                  <option key={typeValue.id} value={typeValue.id}>
                                    {getTranslatedValue(typeValue.value)}
                                  </option>
                                ))}
                              </select>
                              
                              <button
                                type="button"
                                onClick={() => openSelectWithImageModal(
                                  supplierType.id,
                                  supplierTypeValue.id,
                                  supplierTypeValue.extraData?.image
                                )}
                                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-sm border flex items-center"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                </svg>
                                View Images
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Image Options Selection Modal */}
      {selectWithImageModalOpen && (
        <Modal
          title={
            <div className="flex items-center">
              {selectWithImageModalState?.supplierTypeValueImage && <img referrerPolicy='no-referrer' src={selectWithImageModalState?.supplierTypeValueImage} className="w-60 h-60 object-cover rounded-sm border" />}
              <span className="ml-2">Select Option with Image</span>
            </div>
          }
          onClose={closeSelectWithImageModal}
          actions={[
            {
              label: 'Cancel',
              onClick: closeSelectWithImageModal,
              preset: 'white'
            }
          ]}
          width="large"
        >
          {selectWithImageModalState && (
            <div className="py-4">
              <div className="mb-4 bg-blue-50 p-3 rounded-lg border border-blue-100">
                <p className="text-blue-800 text-sm">
                  Click on an option to select it and close this dialog.
                </p>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {typesMappings[selectWithImageModalState.supplierTypeId]?.mappedAdTypeId && queryData.ad!.variationTypes
                  .find((t: any) => t.id === typesMappings[selectWithImageModalState.supplierTypeId]?.mappedAdTypeId)
                  ?.typeValues.map((typeValue: any) => {
                    const isSelected = typesValuesMappings[selectWithImageModalState.supplierTypeValueId]?.mappedAdTypeValueId === typeValue.id;
                    
                    return (
                      <div 
                        key={typeValue.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                          isSelected ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-300' : 'hover:border-gray-400'
                        }`}
                        onClick={() => selectOptionFromModal(typeValue.id)}
                      >
                        <div className="flex flex-col items-center space-y-3">
                          {typeValue.medias && typeValue.medias.length > 0 ? (
                            <div className="relative w-full pb-[100%]"> {/* Aspect ratio container */}
                              <img 
                                src={typeValue.medias[0].url} 
                                alt={getTranslatedValue(typeValue.value)}
                                className="absolute inset-0 h-full w-full object-contain rounded-md"
                              />
                            </div>
                          ) : (
                            <div className="w-full pb-[100%] relative bg-gray-200 rounded-md">
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-sm text-gray-500">No image</span>
                              </div>
                            </div>
                          )}
                          
                          <div className="text-center">
                            <div className="font-medium">{getTranslatedValue(typeValue.value)}</div>
                            {isSelected && (
                              <div className="text-sm text-blue-600 flex items-center justify-center mt-1">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Selected
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            </div>
          )}
        </Modal>
      )}
    </>
  );
} 